import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Shell";

const GoogleButton = () => (
  <button
    type="button"
    onClick={() => {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + "/";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }}
    className="ds-btn-ghost w-full"
    data-testid="google-login-button"
  >
    <img src="https://www.google.com/favicon.ico" alt="" className="h-4 w-4" />
    Continuer avec Google
  </button>
);

const AuthShell = ({ title, subtitle, children }) => (
  <div className="grid min-h-screen bg-white md:grid-cols-2">
    <div className="hidden flex-col justify-between bg-brand p-10 text-white md:flex">
      <Brand compact />
      <div>
        <h1 className="font-display text-4xl font-extrabold leading-tight">
          Achetez au détail<br />ou en gros,<br />
          <span className="text-gold">livré en Guinée.</span>
        </h1>
        <p className="mt-4 max-w-sm text-sm text-emerald-100">
          Entrepôt de 300 m² en Guinée. Livraison Entrepôt, Coyah, Dubréka et régions.
          Paiement avant livraison : Orange Money, MTN ou virement.
        </p>
      </div>
      <p className="text-xs text-emerald-200">© {new Date().getFullYear()} EMPIRE-SHOP Guinée</p>
    </div>
    <div className="flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm ds-rise">
        <div className="md:hidden">
          <Brand />
        </div>
        <h2 className="mt-6 font-display text-2xl font-bold text-slate-900">{title}</h2>
        <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
        <div className="mt-6 space-y-4">{children}</div>
      </div>
    </div>
  </div>
);

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const res = await login(form.email, form.password);
    setBusy(false);
    if (res.error) {
      setError(res.error);
      return;
    }
    toast.success(`Bienvenue ${res.user.name} !`);
    const target =
      res.user.role === "admin" ? "/admin" : res.user.role === "warehouse" ? "/admin/stock" : location.state?.from || "/";
    navigate(target, { replace: true });
  };

  return (
    <AuthShell title="Connexion" subtitle="Accédez à votre compte EMPIRE-SHOP">
      <form onSubmit={submit} className="space-y-3" data-testid="login-form">
        <div>
          <label className="ds-label">Email</label>
          <input
            type="email"
            required
            className="ds-input mt-1"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="login-email-input"
          />
        </div>
        <div>
          <label className="ds-label">Mot de passe</label>
          <input
            type="password"
            required
            className="ds-input mt-1"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            data-testid="login-password-input"
          />
        </div>
        {error && (
          <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="login-error">
            {error}
          </p>
        )}
        <button type="submit" disabled={busy} className="ds-btn-primary w-full" data-testid="login-submit-button">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Se connecter
        </button>
      </form>
      <div className="flex items-center gap-3 text-xs text-slate-400">
        <span className="h-px flex-1 bg-slate-200" /> ou <span className="h-px flex-1 bg-slate-200" />
      </div>
      <GoogleButton />
      <p className="text-center text-sm text-slate-600">
        Pas encore de compte ?{" "}
        <Link to="/inscription" className="font-semibold text-brand" data-testid="go-to-register">
          Créer un compte
        </Link>
      </p>
    </AuthShell>
  );
}

export function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", address: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const res = await register(form);
    setBusy(false);
    if (res.error) {
      setError(res.error);
      return;
    }
    toast.success("Compte créé. Bienvenue chez EMPIRE-SHOP !");
    navigate("/", { replace: true });
  };

  return (
    <AuthShell title="Créer un compte" subtitle="Quelques secondes suffisent pour commander">
      <form onSubmit={submit} className="space-y-3" data-testid="register-form">
        <div>
          <label className="ds-label">Nom complet</label>
          <input
            required
            className="ds-input mt-1"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="register-name-input"
          />
        </div>
        <div>
          <label className="ds-label">Email</label>
          <input
            type="email"
            required
            className="ds-input mt-1"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="register-email-input"
          />
        </div>
        <div>
          <label className="ds-label">Téléphone</label>
          <input
            className="ds-input mt-1"
            placeholder="+224 6XX XX XX XX"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            data-testid="register-phone-input"
          />
        </div>
        <div>
          <label className="ds-label">Adresse de livraison</label>
          <input
            className="ds-input mt-1"
            placeholder="Quartier, commune, repère"
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
            data-testid="register-address-input"
          />
        </div>
        <div>
          <label className="ds-label">Mot de passe (6 caractères min.)</label>
          <input
            type="password"
            required
            minLength={6}
            className="ds-input mt-1"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            data-testid="register-password-input"
          />
        </div>
        {error && (
          <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="register-error">
            {error}
          </p>
        )}
        <button type="submit" disabled={busy} className="ds-btn-primary w-full" data-testid="register-submit-button">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Créer mon compte
        </button>
      </form>
      <GoogleButton />
      <p className="text-center text-sm text-slate-600">
        Déjà inscrit ?{" "}
        <Link to="/connexion" className="font-semibold text-brand" data-testid="go-to-login">
          Se connecter
        </Link>
      </p>
    </AuthShell>
  );
}
